import {
  ChevronLeftIcon,
  useReducedMotion,
  WordsHappenButton,
  WordsHappenTab,
  WordsHappenTabList,
  WordsHappenTabPanel,
  WordsHappenTabs,
} from '@words-happen/ui-components';
import type React from 'react';
import { useRef, useState } from 'react';
import { type AccountOption, searchAccountOptions } from '../account-options';
import { ProfilePanel, type ProfilePanelProps, profileSearchEntries } from '../profile';
import { ScreenFrame } from '../screen-frame';
import { SettingsPanel, type SettingsPanelProps, settingsSearchEntries } from '../settings';
import styles from './account-screen.module.scss';
import { AccountSearch } from './account-search';
import { ACCOUNT_TABS, type AccountTab, isAccountTab } from './account-tabs';
import { useOptionFocus } from './use-option-focus';

export interface AccountScreenProps {
  /** The tab shown. Each tab has its own route, so the app passes the route's tab in. */
  tab: AccountTab;
  /** Called with a newly chosen tab: the app moves to its route. */
  onTabChange: (tab: AccountTab) => void;
  /** The back control: returns to the dashboard. */
  onBack: () => void;
  profile: ProfilePanelProps['account'];
  settings: Omit<SettingsPanelProps, 'className'>;
  className?: string;
}

/** A guest's sign-in, from whichever tab has one. */
const guestSignIn = (profile: AccountScreenProps['profile'], settings: AccountScreenProps['settings']) => {
  if (settings.account.kind === 'guest') return settings.account.onSignIn;
  if (profile.kind === 'guest') return profile.onSignIn;
  return undefined;
};

/**
 * The account area: a back control, a search across both tabs, and two tabs, Profile (/profile) and Settings
 * (/settings). The app passes in the tab from the route and moves to the chosen tab's route, so each tab works as a
 * deep link and back and forward move between them. While there's a query the tabs give way to the matches; choosing
 * one clears the search, opens its tab and moves focus to it.
 */
export const AccountScreen: React.FC<AccountScreenProps> = ({
  tab,
  onTabChange,
  onBack,
  profile,
  settings,
  className,
}) => {
  const main = useRef<HTMLElement>(null);
  const [query, setQuery] = useState('');
  const searching = query.trim() !== '';
  const focusOption = useOptionFocus(main, tab, !searching, useReducedMotion());
  const result = searching
    ? searchAccountOptions([...settingsSearchEntries(settings.account), ...profileSearchEntries(profile)], query)
    : undefined;

  const choose = (option: AccountOption) => {
    setQuery('');
    focusOption(option);
    if (option.tab !== tab) onTabChange(option.tab);
  };

  return (
    <ScreenFrame className={className}>
      <header className={styles.topBar}>
        <WordsHappenButton
          size="sm"
          className={styles.back}
          aria-label="Back to home"
          icon={<ChevronLeftIcon size={22} />}
          onClick={onBack}
        />
      </header>
      <main ref={main} className={styles.main}>
        <h1 className={styles.title}>Your account</h1>
        <AccountSearch
          query={query}
          onQueryChange={(next) => {
            setQuery(next);
            focusOption(undefined);
          }}
          result={result}
          onChoose={choose}
          onSignIn={guestSignIn(profile, settings)}
        />
        {searching ? null : (
          <WordsHappenTabs
            className={styles.tabs}
            value={tab}
            onChange={(next) => {
              if (isAccountTab(next)) onTabChange(next);
            }}
          >
            <WordsHappenTabList aria-label="Account">
              {ACCOUNT_TABS.map(({ value, label }) => (
                <WordsHappenTab key={value} value={value}>
                  {label}
                </WordsHappenTab>
              ))}
            </WordsHappenTabList>
            <WordsHappenTabPanel className={styles.panel} value="profile">
              <ProfilePanel account={profile} />
            </WordsHappenTabPanel>
            <WordsHappenTabPanel className={styles.panel} value="settings">
              <SettingsPanel {...settings} />
            </WordsHappenTabPanel>
          </WordsHappenTabs>
        )}
      </main>
    </ScreenFrame>
  );
};
